/**
 * Pure transforms over a studio-config draft.
 *
 * `edit_studio_config` is FULL-REPLACE: the patch a surface sends BECOMES the
 * stored override row. There is a wipe guard, but it only reaches TOP-LEVEL
 * keys — `findStudioConfigWipedField` skips any key that is present and
 * non-null, so a patch supplying `chat_translator: {…}` while dropping the
 * sub-key `send_quick_ack` sails straight through it. `l2-yaml.ts` says as much
 * about `/config` itself: it "POSTs the whole config object and so round-trips
 * the block by accident".
 *
 * Accident is not a mechanism. These transforms exist so a second page can edit
 * one field without inheriting that risk: each takes the WHOLE config, returns
 * the WHOLE config, and changes exactly one path. Nothing here constructs a
 * partial patch, and nothing here can.
 *
 * Every choice is written as an explicit VALUE, never as a deleted key. The live
 * config is the git YAML with the override row deep-merged over it, and
 * `deepMergeStudioConfigOverride` keeps every baseline key the override lacks,
 * so deleting a key cannot undo a value git sets. The Save answers 200, the page
 * shows the choice from its own cache, and the next read shows the git value
 * again. Until 2026-10-07 these transforms dropped each key at its code default,
 * and the Clavis tenant could not switch back on any of the five connectors its
 * YAML pauses (pinned end to end by
 * `apps/dashboard/tests/studio-config-choice-survives-save.test.ts`).
 * `studio.ops_alert_whatsapp: null` and `studio.webchat_enabled: false` were
 * already written this way for the same reason. A control nobody touches still
 * writes nothing.
 *
 * `StudioConfig.tsx` and `Agents.tsx` both call these, so the two pages cannot
 * disagree about what a choice writes.
 */

import type {
  AiAutonomyMode,
  ConnectorCapability,
  ConnectorKey,
  StripeOutboundEnvironment,
} from "@layer/canonical-data/connectors";
import type { PictureIntakeMode } from "@layer/canonical-data/picture-intake-mode";
import {
  SESSION_FEEDBACK_CADENCE_MAX,
  resolveSessionFeedbackCadence,
  type SessionFeedbackCadence,
} from "@layer/canonical-data/session-feedback-cadence";
import {
  TRAINER_REVIEW_CALL_DAY_MAX,
  TRAINER_REVIEW_CALL_DAY_MIN,
  TRAINER_REVIEW_CALL_MINUTES_CHOICES,
  TRAINER_REVIEW_SEND_DAY_MAX,
  TRAINER_REVIEW_SEND_DAY_MIN,
  TRAINER_REVIEW_SEND_HOUR_MAX,
  TRAINER_REVIEW_SEND_HOUR_MIN,
  resolveTrainerReviewSchedule,
  trainerReviewCallClockMinutes,
  type TrainerReviewSchedule,
} from "@layer/canonical-data/trainer-review-schedule";

/**
 * The four states a conversational connector's row can be in. The first is the
 * connector pause; the other three are {@link AiAutonomyMode} values, so this
 * union is deliberately `"off" | AiAutonomyMode` rather than a fourth
 * vocabulary.
 */
export type ConnectorAiState = "off" | AiAutonomyMode;

interface ConnectorEntry {
  enabled?: boolean;
  autonomy?: AiAutonomyMode;
  capability?: ConnectorCapability;
}

/**
 * Write `fields` into one connector's entry, keeping its other sub-keys and
 * every other connector. The three connector transforms touch DISJOINT
 * sub-keys of the same entry, so applying one never disturbs another.
 */
function withConnectorFields<T extends Record<string, unknown>>(
  config: T,
  key: ConnectorKey,
  fields: ConnectorEntry,
): T {
  const connectors = (config["connectors"] ?? {}) as Partial<
    Record<ConnectorKey, ConnectorEntry>
  >;
  // Spread the WHOLE config, then replace one key. Every unrelated top-level
  // block — and every sub-key inside them — survives by construction rather
  // than by the author remembering to carry it.
  const draft: Record<string, unknown> = { ...config };
  draft["connectors"] = {
    ...connectors,
    [key]: { ...(connectors[key] ?? {}), ...fields },
  };
  return draft as T;
}

/**
 * Set one conversational connector's four-state ladder on a config draft.
 *
 * On writes `enabled: true` beside the chosen `autonomy`, both explicit even at
 * the connector's own default, so neither a git pause nor a git mode can win
 * over the choice. Turning the connector OFF leaves the stored autonomy — and
 * the stored capability — untouched, so switching it back on restores the
 * operator's previous choices rather than silently resetting them.
 */
export function applyConnectorState<T extends Record<string, unknown>>(
  config: T,
  key: ConnectorKey,
  next: ConnectorAiState,
): T {
  return withConnectorFields(
    config,
    key,
    next === "off" ? { enabled: false } : { enabled: true, autonomy: next },
  );
}

/**
 * Set one connector's capability pair on a config draft — explicit even at the
 * connector's own default, for the same reason as {@link applyConnectorState}.
 * Touches `capability` only, so the off/on toggle preserves it.
 */
export function applyConnectorCapability<T extends Record<string, unknown>>(
  config: T,
  key: ConnectorKey,
  next: ConnectorCapability,
): T {
  return withConnectorFields(config, key, { capability: next });
}

/**
 * Pause or resume a plain (non-conversational) connector on a config draft.
 * Resuming writes `enabled: true` rather than dropping the flag, so a connector
 * the git YAML pauses can be switched back on.
 */
export function applyConnectorEnabled<T extends Record<string, unknown>>(
  config: T,
  key: ConnectorKey,
  enabled: boolean,
): T {
  return withConnectorFields(config, key, { enabled });
}

/**
 * Choose the Stripe account outbound money actions run against. `test` is
 * written explicitly: dropping it would let a git `live` win, and the page would
 * show the sandbox while real cards were charged.
 */
export function applyStripeOutboundEnvironment<
  T extends Record<string, unknown>,
>(config: T, env: StripeOutboundEnvironment): T {
  const stripe = (config["stripe"] ?? {}) as Record<string, unknown>;
  const draft: Record<string, unknown> = { ...config };
  draft["stripe"] = { ...stripe, outbound_environment: env };
  return draft as T;
}

/** The two catalogs `catalog_visibility` is keyed by. */
export type CatalogVisibilityKind = "packages" | "memberships";

/** The two audiences a catalog entry can be shown to or hidden from. */
export type CatalogVisibilityAudience = "members" | "staff";

interface CatalogEntryFlags {
  members?: boolean;
  staff?: boolean;
}

/**
 * Show or hide one package or membership from one audience. Showing writes
 * `true` rather than dropping the flag, so an entry the git YAML hides can be
 * shown again; the entry's other audience and every other slug are kept.
 */
export function applyCatalogVisibility<T extends Record<string, unknown>>(
  config: T,
  kind: CatalogVisibilityKind,
  slug: string,
  audience: CatalogVisibilityAudience,
  visible: boolean,
): T {
  const cv = (config["catalog_visibility"] ?? {}) as Partial<
    Record<CatalogVisibilityKind, Record<string, CatalogEntryFlags>>
  >;
  const kindMap = cv[kind] ?? {};
  const draft: Record<string, unknown> = { ...config };
  draft["catalog_visibility"] = {
    ...cv,
    [kind]: {
      ...kindMap,
      [slug]: { ...(kindMap[slug] ?? {}), [audience]: visible },
    },
  };
  return draft as T;
}

/**
 * Set `studio.webchat_enabled` on a config draft.
 *
 * The `studio` block is where the sub-key blind spot bites hardest: it carries
 * ~20 unrelated fields (ops-alert number, template names, brand voice, opening
 * hours), and the wipe guard sees only that `studio` is present. Spreading the
 * existing block is what keeps them.
 *
 * `false` is written explicitly rather than dropped: absent means OFF at the
 * endpoint, so dropping the key would also turn it off — but it would be
 * indistinguishable from "never configured", and an operator who turns webchat
 * off should be able to see that they did.
 */
export function applyWebchatEnabled<T extends Record<string, unknown>>(
  config: T,
  enabled: boolean,
): T {
  const studio = (config["studio"] ?? {}) as Record<string, unknown>;
  const draft: Record<string, unknown> = { ...config };
  draft["studio"] = { ...studio, webchat_enabled: enabled };
  return draft as T;
}

/** How the trainer flows (T1 + T3) route relative to the admin number(s). */
export type TrainerCommsAdminMode = "off" | "redirect" | "copy";

/**
 * Set `studio.trainer_comms_admin_mode` on a config draft. `off` is written
 * explicitly: under a git `redirect` the morning brief goes to the admin
 * INSTEAD of the trainer, so an `off` that a git value can overrule would leave
 * every trainer unbriefed while the page reads "off". Spreads the `studio`
 * block for the same reason as {@link applyWebchatEnabled}.
 */
export function applyTrainerCommsAdminMode<T extends Record<string, unknown>>(
  config: T,
  mode: TrainerCommsAdminMode,
): T {
  const studio = (config["studio"] ?? {}) as Record<string, unknown>;
  const draft: Record<string, unknown> = { ...config };
  draft["studio"] = { ...studio, trainer_comms_admin_mode: mode };
  return draft as T;
}

/**
 * Set `studio.picture_intake` on a config draft (whatsapp-picture-intake,
 * 2026-10-14). Every mode is written explicitly, `off` included, for the
 * reason {@link applyTrainerCommsAdminMode} gives: the live config is the git
 * YAML with this override merged over it, so a dropped key would let a git
 * `staff` or `both` keep the intake on while the page reads "off". Spreads the
 * `studio` block for the same reason as {@link applyWebchatEnabled}.
 */
export function applyPictureIntake<T extends Record<string, unknown>>(
  config: T,
  mode: PictureIntakeMode,
): T {
  const studio = (config["studio"] ?? {}) as Record<string, unknown>;
  const draft: Record<string, unknown> = { ...config };
  draft["studio"] = { ...studio, picture_intake: mode };
  return draft as T;
}

/**
 * Set `studio.instagram_funnel_approval_whatsapp` on a config draft.
 *
 * Its own helper rather than a generic `setStudioBoolean`, for the same reason
 * {@link applyWebchatEnabled} is one: the `studio` block carries ~20 unrelated
 * fields and the full-replace save's wipe guard only reaches TOP-LEVEL keys, so a
 * patch that rebuilt `studio` from the one field it cares about would silently
 * drop the ops-alert number, the template names, the brand voice and the opening
 * hours. Spreading the existing block is what keeps them — pinned by that test's
 * own negative control, case 11.
 *
 * `false` is written explicitly rather than dropped: absent means OFF at the
 * saga's config port, so dropping the key would also turn it off — but it would
 * be indistinguishable from "never configured", and an operator who switches the
 * approval WhatsApp off should be able to see that they did.
 */
export function applyInstagramFunnelApprovalWhatsapp<
  T extends Record<string, unknown>,
>(config: T, enabled: boolean): T {
  const studio = (config["studio"] ?? {}) as Record<string, unknown>;
  const draft: Record<string, unknown> = { ...config };
  draft["studio"] = {
    ...studio,
    instagram_funnel_approval_whatsapp: enabled,
  };
  return draft as T;
}

/**
 * Set `studio.whatsapp_marketing_skip_us_numbers` on a config draft
 * (deliverability-ladder D7, 2026-10-31): whether WhatsApp marketing leaves
 * US numbers out, since Meta does not deliver marketing templates to them.
 *
 * Its own helper, spreading the rest of the `studio` block, for the reason
 * {@link applyInstagramFunnelApprovalWhatsapp} gives; and `false` is written
 * explicitly, so switching it off on the page overrules a git `true` (the
 * override merges over the baseline key by key, so a dropped key would leave
 * the git value in force).
 */
export function applyWhatsAppMarketingSkipUsNumbers<
  T extends Record<string, unknown>,
>(config: T, enabled: boolean): T {
  const studio = (config["studio"] ?? {}) as Record<string, unknown>;
  const draft: Record<string, unknown> = { ...config };
  draft["studio"] = {
    ...studio,
    whatsapp_marketing_skip_us_numbers: enabled,
  };
  return draft as T;
}

/**
 * Set `studio.email_retire_dead_addresses` on a config draft
 * (deliverability-ladder D5, 2026-11-01): whether the morning sweep takes dead
 * email addresses off their members.
 *
 * Its own helper, spreading the rest of the `studio` block, for the reason
 * {@link applyInstagramFunnelApprovalWhatsapp} gives; and `false` is written
 * explicitly, so switching it off on the page overrules any git `true` (the
 * override merges over the baseline key by key, so a dropped key would leave
 * the git value in force).
 */
export function applyEmailRetireDeadAddresses<
  T extends Record<string, unknown>,
>(config: T, enabled: boolean): T {
  const studio = (config["studio"] ?? {}) as Record<string, unknown>;
  const draft: Record<string, unknown> = { ...config };
  draft["studio"] = {
    ...studio,
    email_retire_dead_addresses: enabled,
  };
  return draft as T;
}

/** One of the two numbers of the session feedback schedule. */
export type SessionFeedbackCadenceField = keyof SessionFeedbackCadence;

/**
 * Set one number of the session feedback schedule (the top-level
 * `session_feedback_request` block, session-feedback-cadence 2026-10-21) on a
 * config draft, from what the operator typed.
 *
 * BOTH numbers are written, the untouched one at its current effective value,
 * so the stored block always states the whole schedule as explicit values —
 * the rule this file's header gives — and a later change to the code defaults
 * cannot move a studio that chose its numbers. The typed text is truncated to
 * a whole number and held inside the range the validator accepts
 * (`first_after_session` 1–100, `then_every_sessions` 0–100); text that is not
 * a number reads as the field's minimum. The `/config` inputs only call this
 * with a non-empty box, so clearing a box to retype it never writes anything.
 * The block's other key, the Google review link (session-rating-review,
 * 2026-11-03), is kept as it is: dropping it would be refused by the wipe
 * guard, and the link is a switch.
 */
export function applySessionFeedbackCadenceField<
  T extends Record<string, unknown>,
>(config: T, field: SessionFeedbackCadenceField, typed: string): T {
  const block = config["session_feedback_request"] as
    | Record<string, unknown>
    | undefined;
  const current = resolveSessionFeedbackCadence(
    block as Partial<SessionFeedbackCadence> | undefined,
  );
  const min = field === "first_after_session" ? 1 : 0;
  const n = Math.trunc(Number(typed));
  const value = Number.isFinite(n)
    ? Math.max(min, Math.min(SESSION_FEEDBACK_CADENCE_MAX, n))
    : min;
  const draft: Record<string, unknown> = { ...config };
  draft["session_feedback_request"] = { ...block, ...current, [field]: value };
  return draft as T;
}

/**
 * Set the studio's Google review link (`session_feedback_request.
 * google_review_url`, session-rating-review 2026-11-03) on a config draft,
 * from what the operator typed. With a link saved, a member who taps a 3 gets
 * a "Review us on Google" button; there is no other switch, so this box IS
 * the switch.
 *
 * The typed text is trimmed; an emptied box writes an explicit `null`, never a
 * deleted key (this file's header rule), so "no link" can stand over a link
 * in the git YAML. The rest of the block is kept as it is, and a schedule the
 * studio never chose is not pinned. The link is not checked here: the page
 * shows `googleReviewUrlProblem`'s reason under the box, and the Save refuses
 * a link the validator would.
 */
export function applySessionFeedbackReviewUrl<
  T extends Record<string, unknown>,
>(config: T, typed: string): T {
  const block = config["session_feedback_request"] as
    | Record<string, unknown>
    | undefined;
  const link = typed.trim();
  const draft: Record<string, unknown> = { ...config };
  draft["session_feedback_request"] = {
    ...block,
    google_review_url: link === "" ? null : link,
  };
  return draft as T;
}

/**
 * Set the monthly trainer review's schedule (the top-level `trainer_reviews`
 * block, trainer-review-lane 2026-10-23) on a config draft: whether it is on,
 * its send day and hour, and (trainer-review-call-time, 2026-10-24) whether
 * trainers pick a call time and the windows those times come from.
 *
 * ALL TEN fields are written, the untouched ones at their current effective
 * values, so the stored block always states the whole schedule as explicit
 * values — the rule this file's header gives. That matters most for the two
 * switches: the git YAML ships `enabled: false`, and a choice of `true` must
 * be stored as `true`, never as a missing key; `call_times` likewise. A value
 * the validator would refuse is held to what it accepts: a day or hour to its
 * range (a fraction truncated), a call length to the nearest choice, an
 * unreadable time or an empty weekday set to the current value, a last call
 * day before the first to the first, and a window too short for one call
 * ends one call after it opens.
 */
export function applyTrainerReviewsChoice<T extends Record<string, unknown>>(
  config: T,
  choice: Partial<TrainerReviewSchedule>,
): T {
  const current = resolveTrainerReviewSchedule(
    config["trainer_reviews"] as Partial<TrainerReviewSchedule> | undefined,
  );
  const clamp = (n: number, min: number, max: number): number => {
    const whole = Math.trunc(n);
    return Number.isFinite(whole) ? Math.max(min, Math.min(max, whole)) : min;
  };
  const clock = (value: string | undefined, fallback: string): string =>
    value !== undefined && trainerReviewCallClockMinutes(value) !== null ? value : fallback;
  const weekdays = (() => {
    const raw = choice.call_weekdays ?? current.call_weekdays;
    const clean = [...new Set(raw.filter((d) => Number.isInteger(d) && d >= 1 && d <= 7))].sort(
      (a, b) => a - b,
    );
    return clean.length > 0 ? clean : [...current.call_weekdays];
  })();
  const minutes = (() => {
    const wanted = choice.call_minutes ?? current.call_minutes;
    return TRAINER_REVIEW_CALL_MINUTES_CHOICES.reduce((best, m) =>
      Math.abs(m - wanted) < Math.abs(best - wanted) ? m : best,
    );
  })();
  const firstDay = clamp(
    choice.call_first_day ?? current.call_first_day,
    TRAINER_REVIEW_CALL_DAY_MIN,
    TRAINER_REVIEW_CALL_DAY_MAX,
  );
  const lastDay = Math.max(
    firstDay,
    clamp(
      choice.call_last_day ?? current.call_last_day,
      TRAINER_REVIEW_CALL_DAY_MIN,
      TRAINER_REVIEW_CALL_DAY_MAX,
    ),
  );
  // A start so late that no call fits before midnight moves back to the last one that does.
  const callFrom = (() => {
    const from = clock(choice.call_from, current.call_from);
    const latest = 23 * 60 + 59 - minutes;
    if ((trainerReviewCallClockMinutes(from) ?? 0) <= latest) return from;
    return `${String(Math.floor(latest / 60)).padStart(2, "0")}:${String(latest % 60).padStart(2, "0")}`;
  })();
  const callTo = (() => {
    const to = clock(choice.call_to, current.call_to);
    const fromMin = trainerReviewCallClockMinutes(callFrom) ?? 0;
    const toMin = trainerReviewCallClockMinutes(to) ?? 0;
    if (toMin - fromMin >= minutes) return to;
    const end = Math.min(fromMin + minutes, 23 * 60 + 59);
    return `${String(Math.floor(end / 60)).padStart(2, "0")}:${String(end % 60).padStart(2, "0")}`;
  })();
  const draft: Record<string, unknown> = { ...config };
  draft["trainer_reviews"] = {
    enabled: choice.enabled ?? current.enabled,
    send_day: clamp(
      choice.send_day ?? current.send_day,
      TRAINER_REVIEW_SEND_DAY_MIN,
      TRAINER_REVIEW_SEND_DAY_MAX,
    ),
    send_hour: clamp(
      choice.send_hour ?? current.send_hour,
      TRAINER_REVIEW_SEND_HOUR_MIN,
      TRAINER_REVIEW_SEND_HOUR_MAX,
    ),
    call_times: choice.call_times ?? current.call_times,
    call_weekdays: weekdays,
    call_from: callFrom,
    call_to: callTo,
    call_first_day: firstDay,
    call_last_day: lastDay,
    call_minutes: minutes,
  };
  return draft as T;
}
